import { isNil } from 'lodash-es';
import { inject, injectable } from 'tsyringe';
import { type ICache } from './Cache';
import { MiddlewareInjectToken } from './constants/tokens';
import { type Apis, CacheLevel, type MiddlewareConfig } from './types';

@injectable()
export class BaseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) protected readonly apis: Apis,
        @inject(MiddlewareInjectToken.Config) protected readonly config: MiddlewareConfig,
    ) {}
}

@injectable()
export class CachedResponseApi extends BaseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) private readonly cache: ICache,
    ) {
        super(apis, config);

        if (config.cacheLevel === CacheLevel.RequestsAndCalculations) {
            const prototype = Object.getPrototypeOf(this);
            const methods = Object.getOwnPropertyNames(prototype).filter((name) => name !== 'constructor');

            for (const method of methods) {
                const originalMethod = (this as any)[method];
                Object.defineProperty(this, method, {
                    value: (...args: any[]) => {
                        const cacheKey = method + ':' + JSON.stringify(args);
                        const response = this.cache.get(cacheKey);

                        if (!isNil(response)) {
                            if (response instanceof Promise) {
                                return response;
                            } else {
                                return Promise.resolve(response);
                            }
                        } else {
                            this.cache.del(cacheKey);

                            let promise = originalMethod.apply(this, args);

                            if (!(promise instanceof Promise)) {
                                promise = Promise.resolve(promise);
                            }

                            promise
                                .then((data: any) => {
                                    this.cache.set(cacheKey, data, this.config.cacheDuration);
                                    return data;
                                })
                                .catch((error: Error) => {
                                    this.cache.del(cacheKey);
                                    throw error;
                                });

                            this.cache.set(cacheKey, promise, this.config.cacheDuration);
                            return promise;
                        }
                    },
                });
            }
        }
    }
}
